import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, TrendingUp, Users, Briefcase, Flag } from 'lucide-react';

const serviceDropdown = [
  { to: '/services/revscale', label: 'RevScale Fractional CSO/CRO', desc: 'CRO, CSO & GTM Strategy', icon: TrendingUp },
  { to: '/services/leadership-hiring', label: 'Leadership Hiring', desc: 'Executive search & placement', icon: Users },
  { to: '/services/product-roadmapping', label: 'Product Roadmapping', desc: 'Marketing, relevance & roadmap', icon: Briefcase },
  { to: '/services/corporate-golf', label: 'Golf for Corporate Success', desc: 'Executive networking on the course', icon: Flag },
];

const navLinks = [
  { to: '/why-fractional', label: 'Why Us' },
  { to: '/roi-calculator', label: 'ROI Calculator' },
  { to: '/testimonials', label: 'Testimonials' },
  { to: '/faq', label: 'FAQ' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setServicesOpen(false), 150);
  }, []);

  const showTransparent = isHome && !scrolled;
  const isServicesActive = location.pathname.startsWith('/services');

  return (
    <header data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        showTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="logo-link" className="flex flex-col">
          <span className={`text-xl lg:text-2xl font-bold tracking-tight transition-colors duration-300 ${showTransparent ? 'text-white' : 'text-primary'}`}>
            Avant-Garde
          </span>
          <span className={`text-[10px] lg:text-xs font-medium uppercase tracking-widest transition-colors duration-300 ${showTransparent ? 'text-white/70' : 'text-muted-foreground'}`}>
            Consulting
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          <div className="relative" ref={dropdownRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button data-testid="nav-services-dropdown"
              className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 ${
                isServicesActive
                  ? showTransparent ? 'text-white' : 'text-primary'
                  : showTransparent ? 'text-white/75 hover:text-white' : 'text-muted-foreground hover:text-foreground'
              }`}>
              Services
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
              servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
            }`}>
              <div className="bg-white rounded-xl shadow-lg border border-border p-2 min-w-[280px]" data-testid="services-dropdown-menu">
                {serviceDropdown.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.to} to={item.to} data-testid={`dropdown-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`flex items-start gap-3 px-4 py-3 rounded-lg transition-colors duration-150 group ${
                        location.pathname === item.to ? 'bg-primary/5 text-primary' : 'hover:bg-muted text-foreground'
                      }`}>
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-primary/15 transition-colors">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{item.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-sm font-medium transition-colors duration-200 ${
                location.pathname === link.to
                  ? showTransparent ? 'text-white' : 'text-primary'
                  : showTransparent ? 'text-white/75 hover:text-white' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {link.label}
            </Link>
          ))}
          <Link to="/contact" data-testid="nav-get-started-btn"
            className="inline-flex items-center justify-center rounded-lg bg-accent text-white font-semibold text-sm px-6 py-2.5 hover:bg-accent/90 transition-colors">
            Get Started
          </Link>
        </nav>

        <button data-testid="mobile-menu-toggle" className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen
            ? <X className={`w-6 h-6 ${showTransparent ? 'text-white' : 'text-foreground'}`} />
            : <Menu className={`w-6 h-6 ${showTransparent ? 'text-white' : 'text-foreground'}`} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border px-4 py-4 space-y-1" data-testid="mobile-menu">
          <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} data-testid="mobile-services-toggle"
            className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isServicesActive ? 'text-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}>
            Services
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileServicesOpen && (
            <div className="pl-4 space-y-1" data-testid="mobile-services-submenu">
              {serviceDropdown.map((item) => (
                <Link key={item.to} to={item.to}
                  className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${
                    location.pathname === item.to ? 'text-primary bg-primary/5 font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}>
                  {item.label}
                </Link>
              ))}
            </div>
          )}
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to}
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === link.to ? 'text-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}>
              {link.label}
            </Link>
          ))}
          <Link to="/contact" className="block text-center mt-3 rounded-lg bg-accent text-white font-semibold text-sm px-6 py-3 hover:bg-accent/90 transition-colors">
            Get Started
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
